import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { Camera, Heart } from "lucide-react";
import ChapterHero from "../components/ChapterHero";
import NextPage from "../components/NextPage";
import SEO from "../components/SEO";
import useCharacterPhoto from "../hooks/useCharacterPhoto";
import { getSaltieFunFacts } from "../lib/api";

/**
 * Round dashed photo placeholder that shows "YOUR PHOTO HERE" until the
 * admin uploads a real photo for the given slug (via /admin → Character
 * Photos).
 */
const FunPhoto = ({ slug, name }) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const rel = useCharacterPhoto(slug);
  const url = rel ? (rel.startsWith("http") ? rel : `${backendUrl}${rel}`) : null;
  return (
    <div className="flex flex-col items-center" data-testid={`fun-photo-${slug}`}>
      <div
        className="relative rounded-full border-2 border-dashed border-[#B4A79A] bg-[#F1E9D9] flex items-center justify-center overflow-hidden"
        style={{ width: "clamp(6rem, 9vw, 8.5rem)", height: "clamp(6rem, 9vw, 8.5rem)" }}
      >
        {url ? (
          <img src={url} alt={name} className="w-full h-full object-cover" draggable="false" />
        ) : (
          <div className="flex flex-col items-center gap-1 text-[#8A7B6D] px-2 text-center">
            <Camera size={22} strokeWidth={1.4} />
            <span className="uppercase tracking-widest text-[9px] leading-tight">
              Your Photo<br />Here
            </span>
          </div>
        )}
      </div>
      <div
        className="mt-3 relative px-4 py-1.5 min-w-[6.5rem] text-center"
        style={{
          background:
            "linear-gradient(180deg, #D6B078 0%, #C08D4E 55%, #A87139 100%)",
          borderRadius: "6px",
          boxShadow:
            "0 3px 0 rgba(80,50,20,0.35), 0 6px 10px -3px rgba(60,35,10,0.3), inset 0 1px 0 rgba(255,236,200,0.55)",
          border: "1px solid #7A5233",
          minHeight: "1.9rem",
        }}
      >
        <div className="flex items-center justify-center gap-1.5">
          <Heart size={12} className="fill-[#D62839] text-[#D62839]" />
          <span
            className="font-display text-[#2A1A0E] uppercase tracking-wide"
            style={{ fontSize: "clamp(0.7rem, 0.85vw, 0.9rem)", letterSpacing: "0.05em" }}
          >
            {name || "\u00A0"}
          </span>
        </div>
      </div>
    </div>
  );
};

/** A single fact tile: icon, uppercase title, short body. */
const FactCard = ({ icon, color, title, body }) => {
  const IconCmp = Icons[icon] || Icons.Sparkles;
  return (
    <div className="flex flex-col items-center text-center px-2" data-testid={`fun-fact-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <IconCmp size={40} strokeWidth={2} style={{ color }} />
      <h3
        className="font-display uppercase mt-3 leading-tight"
        style={{ color, fontSize: "clamp(0.85rem, 1.05vw, 1.05rem)", letterSpacing: "0.04em" }}
      >
        {title}
      </h3>
      <p
        className="font-body mt-2 text-[#3E2F24] leading-snug"
        style={{ fontSize: "clamp(0.78rem, 0.9vw, 0.95rem)" }}
      >
        {body}
      </p>
    </div>
  );
};

/** Section header with heart accents and pink/purple hand-lettered look. */
const SectionHeader = ({ line1, line2 }) => (
  <div className="flex items-center justify-center gap-6 my-4">
    <Heart size={28} className="fill-[#E85DA1] text-[#E85DA1]" />
    <h2
      className="font-display text-center uppercase leading-[0.95]"
      style={{
        fontSize: "clamp(2rem, 4vw, 3.4rem)",
        letterSpacing: "0.02em",
      }}
    >
      <span className="block" style={{ color: "#6A2CB2" }}>{line1}</span>
      <span className="block" style={{ color: "#E85DA1" }}>{line2}</span>
    </h2>
    <Heart size={28} className="fill-[#E85DA1] text-[#E85DA1]" />
  </div>
);

export default function SaltieFunFacts() {
  const [ff, setFf] = useState(null);
  useEffect(() => {
    getSaltieFunFacts().then(setFf).catch(() => setFf(null));
  }, []);

  if (!ff) {
    return (
      <div className="parchment min-h-screen pt-40 text-center text-[#4B3A2F]">
        Loading the pasture…
      </div>
    );
  }

  return (
    <div className="parchment min-h-screen" data-testid="saltie-fun-facts-page">
      <SEO
        title="Saltie Fun Facts"
        description="A colorful infographic of fun facts about our Highland cattle and donkeys — from why they have long bangs to why donkeys are natural guardians."
        path="/saltie-fun-facts"
      />

      <ChapterHero
        path="/saltie-fun-facts"
        image="/assets/heroes/saltie-fun-facts.webp"
        eyebrow={
          <>
            <span style={{ color: "#6A2CB2" }}>Chapter</span>{" "}
            <span style={{ color: "#E85DA1" }}>VI</span>
          </>
        }
        title={
          <span>
            <span style={{ color: "#6A2CB2" }}>Saltie</span>{" "}
            <span style={{ color: "#E85DA1" }}>Fun</span>{" "}
            <span style={{ color: "#4CAF50" }}>Facts</span>
          </span>
        }
        titleAlt="Saltie Fun Facts"
        subtitle={
          <>
            <span style={{ color: "#6A2CB2" }}>Real</span>{" "}
            <span style={{ color: "#E85DA1" }}>animals</span>{" "}
            <span style={{ color: "#4B3A2F" }}>·</span>{" "}
            <span style={{ color: "#4CAF50" }}>Real</span>{" "}
            <span style={{ color: "#579DE6" }}>love</span>{" "}
            <span style={{ color: "#4B3A2F" }}>·</span>{" "}
            <span style={{ color: "#E85D5D" }}>Real</span>{" "}
            <span style={{ color: "#C08D4E" }}>stories</span>
            <span style={{ color: "#4B3A2F" }}>.</span>
          </>
        }
        position="top-left"
        contentStyle={{ marginLeft: "50vw" }}
        overlay={
          <img
            src="/assets/lil-salties-logo.png"
            alt=""
            aria-hidden="true"
            className="absolute pointer-events-none select-none"
            draggable="false"
            style={{
              left: "23%",
              top: "65%",
              width: "18%",
              transform: "translate(-50%, -50%) rotate(-1.5deg)",
              filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.45))",
            }}
          />
        }
      />

      <div className="max-w-6xl mx-auto px-6 -mt-8 relative z-10 pb-24">
        {/* ---------- MOO FACTS ---------- */}
        <SectionHeader line1="Lil Saltie" line2="Moo Facts" />

        <div
          className="grid gap-4 mt-6"
          style={{ gridTemplateColumns: `repeat(${ff.moo_characters.length}, minmax(0, 1fr))` }}
          data-testid="fun-moo-photos"
        >
          {ff.moo_characters.map((c) => (
            <FunPhoto key={c.slug} slug={c.slug} name={c.name} />
          ))}
        </div>

        <div className="border-t border-[#D9C9A8] mt-10 pt-8">
          <div
            className="grid gap-6"
            style={{ gridTemplateColumns: `repeat(${ff.moo_facts.length}, minmax(0, 1fr))` }}
            data-testid="fun-moo-facts"
          >
            {ff.moo_facts.map((f, i) => (
              <FactCard key={i} {...f} />
            ))}
          </div>
        </div>

        {/* ---------- BRAY FACTS ---------- */}
        <div className="mt-20">
          <SectionHeader line1="Lil Saltie" line2="Bray Facts" />
        </div>

        <div
          className="grid gap-4 mt-6"
          style={{ gridTemplateColumns: `repeat(${ff.bray_characters.length}, minmax(0, 1fr))` }}
          data-testid="fun-bray-photos"
        >
          {ff.bray_characters.map((c) => (
            <FunPhoto key={c.slug} slug={c.slug} name={c.name} />
          ))}
        </div>

        <div className="border-t border-[#D9C9A8] mt-10 pt-8">
          <div
            className="grid gap-6"
            style={{ gridTemplateColumns: `repeat(${ff.bray_facts.length}, minmax(0, 1fr))` }}
            data-testid="fun-bray-facts"
          >
            {ff.bray_facts.map((f, i) => (
              <FactCard key={i} {...f} />
            ))}
          </div>
        </div>

        {/* ---------- purple ribbon footer ---------- */}
        <div className="mt-20 flex justify-center" data-testid="fun-facts-ribbon">
          <div
            className="px-10 py-4 text-center"
            style={{
              background: "linear-gradient(180deg, #B08CE5 0%, #8150DB 100%)",
              borderRadius: "8px",
              color: "#FDF6E7",
              boxShadow: "0 4px 12px -2px rgba(90,55,150,0.4)",
            }}
          >
            <p className="font-script text-2xl md:text-3xl">
              Real animals. Real love. Real stories. <Heart size={20} className="inline fill-[#FDF6E7] text-[#FDF6E7] ml-1" />
            </p>
            <p className="uppercase tracking-[0.35em] text-xs md:text-sm mt-1 opacity-90">
              thesaltymoo.com
            </p>
          </div>
        </div>
      </div>

      <NextPage to="/front-porch" label="Take the Front Porch Swing" />
    </div>
  );
}
